// src/content/2026/insights/two-years-of-listening.jsx

import ExternalLink from "../../../components/insights/ExternalLink";
import Image from "../../../components/insights/Image";
import Quote from "../../../components/insights/Quote";
import Citation from "../../../components/insights/Citation";
import References from "../../../components/insights/References";

export const article = {
  slug: "two-years-of-listening",

  meta: {
    title:
      "Two Years of Listening",

    publishedAt: "2026-05-20",

    category: "Voice science",

    author: [
      {
        name: "Neus Calaf",
        role: "Founder, All-Voiced",
      },
    ],

    excerpt:
      "A retrospective reflection on how simple perceptual voice evaluation exercises gradually evolved into broader questions about infrastructure, coordination, and cumulative listening.",

    coverImage:
      "/images/insights/2026/two-years-of-listening/headphones.png",

    references: {
      ref1:
        "Walden, P. R. (2022), “Perceptual Voice Qualities Database (PVQD)”, Mendeley Data, V4. https://doi.org/10.17632/9dz247gnyb.2. ",
      
      ref2:
        "Calaf, N., & Garcia-Quintana, D. (2024). Development and validation of the bilingual Catalan/Spanish cross-cultural adaptation of the Consensus Auditory-Perceptual Evaluation of Voice. Journal of Speech, Language, and Hearing Research, 67(4), 1072–1089. https://doi.org/10.1044/2024_JSLHR-23-00536",
      
      ref3:
        "Kempster, G. B., Gerratt, B. R., Verdolini Abbott, K., Barkmeier-Kraemer, J., & Hillman, R. E. (2009). Consensus auditory-perceptual evaluation of voice: Development of a standardized clinical protocol. American Journal of Speech-Language Pathology, 18(2), 124–132. https://doi.org/10.1044/1058-0360(2008/08-0017)",
      
      ref4:
        "Kempster, G. B., Nagle, K. F., & Solomon, N. P. (2025). Development and rationale for the Consensus Auditory-Perceptual Evaluation of Voice – Revised (CAPE-Vr). Journal of Voice. https://doi.org/10.1016/j.jvoice.2025.01.022",
      
      ref5:
        "Pommée, T., Younes, S., Gauthier, L., & Verduyckt, I. (2025). Improving auditory-perceptual voice assessment: A hybrid in-class and online CAPE-V training study. Journal of Voice. https://doi.org/10.1016/j.jvoice.2025.11.029",
    },
  },

  tags: [
    "All-Voiced",
    "voice-science",
    "auditory-perceptual-evaluation",
    "CAPE-V",
    "infrastructure",
    "education",
  ],

  related: [],

  wordCount: 936,

  Component: function Article({ references }) {
    return (
      <>
        <p>
          Two years ago, around World Voice Day 2024, I published the first
          perceptual training exercises on{" "}
          <ExternalLink href="https://www.all-voiced.com/">
            All-Voiced
          </ExternalLink>.
        </p>

        <p>
          They were extremely simple:
          sliders for overall severity,
          roughness,
          breathiness,
          and strain,
          with feedback based on perceptual reference data from the PVQD<Citation id="ref1" />.
        </p>

        <p>
          Those first exercises emerged from slightly earlier experimental work.
        </p>

        <p>
          At the time, I had started building small digital environments for
          auditory-perceptual voice experiments because I could not find platforms
          flexible enough to support the kinds of perceptual tasks and workflows I
          wanted to explore in research and training contexts.
        </p>

        <p>
          Initially, the goal was mostly practical:
          creating interactive digital environments where perceptual evaluation tasks,
          experiments,
          and listening exercises could be carried out more flexibly.
        </p>

        <p>
          As the project evolved,
          additional perceptual tools and training environments were progressively
          incorporated,
          including the digitalization of CAPE-V<Citation id="ref3" />,
          some of its linguistic adaptations,
          and later CAPE-Vr<Citation id="ref4" />.
        </p>

        <Quote>
          Looking back now, I realize those early listening environments were also the
          beginning of broader questions about how perceptual evaluation could become
          more structured, connected, and reusable over time.
        </Quote>

        <h2>
          Before All-Voiced
        </h2>

        <p>
          Long before All-Voiced existed, auditory-perceptual voice evaluation
          was already central to my clinical, teaching, and research work.
        </p>

        <p>
          During my PhD on the Spanish and Catalan adaptation of the CAPE-V
          <Citation id="ref2" />,
          the evaluation workflow involved printing forms,
          organizing distributed evaluations,
          mailing materials to raters,
          manually measuring visual analog scales,
          and transferring perceptual judgments into spreadsheets.
        </p>

        <p>
          Similar workflows also shaped my teaching activities.
        </p>

        <p>
          Before perceptual evaluation classes,
          I often found myself preparing large numbers of printed forms,
          pencils,
          erasers,
          sharpeners,
          and rulers for students working with visual analog scales.
        </p>

        <Image
          src="/images/insights/2026/two-years-of-listening/ruler.png"
          caption="Manual measurement tools used in auditory-perceptual voice evaluation workflows."
        />

        <p>
          At the time,
          I mostly experienced these workflows as an operational frustration.
        </p>

        <p>
          But in retrospect,
          they were also revealing how difficult perceptual evaluation remained to
          structure,
          coordinate,
          and reuse systematically across contexts and over time.
        </p>

        <p>
          The first digital environments solved some practical problems,
          but they did not yet solve the deeper issue of continuity.
        </p>

        <p>
          Perceptual evaluations could now be carried out digitally,
          yet they still largely functioned as isolated listening experiences:
          completed,
          consumed,
          and then lost.
        </p>

        <h2>
          When listening became cumulative
        </h2>

        <p>
          A major shift occurred in late 2024,
          when All-Voiced introduced its first backend architecture during the{" "}
          <ExternalLink href="https://www.pevoc.org/2024.php">
            Pan-European Voice Conference (PEVoC)
          </ExternalLink>{" "}
          held in Santander.
        </p>

        <p>
          For the first time,
          perceptual evaluations could persist across connected user profiles and
          shared evaluation workflows.
        </p>

        <p>
          Perceptual evaluation was starting to become traceable,
          longitudinal,
          and collectively cumulative.
        </p>

        <Quote>
          Once perceptual evaluation becomes cumulative,
          entirely new forms of coordination,
          calibration,
          and research become possible.
        </Quote>

        <p>
          In May 2025,
          preliminary results from the Global Database of Perceptual Voice
          Assessments were presented at the{" "}
          <ExternalLink href="https://voicefoundation.org/annual-symposium/">
            Voice Foundation Symposium
          </ExternalLink>{" "}
          in Philadelphia,
          where the presentation received the{" "}
          <ExternalLink href="https://voicefoundation.org/annual-symposium/awards-recognizing-excellence/hamdan-international-presenter-award/">
            Hamdan International Presenter Award
          </ExternalLink>.
        </p>

        <p>
          For the first time,
          perceptual variability,
          consensus,
          calibration,
          and evaluator behavior could begin functioning as analyzable structured data
          collected across distributed contexts.
        </p>

        <h2>
          From workflows to infrastructure
        </h2>

        <p>
          As perceptual evaluations became persistent and reusable,
          new educational and collaborative possibilities also started emerging.
        </p>

        <p>
          Educational experiences combining in-class and online perceptual training
          workflows began to be carried out through a collaboration with{" "}
          <ExternalLink href="https://www.umontreal.ca/">
            Université de Montréal
          </ExternalLink>{" "}
          using All-Voiced.
        </p>

        <p>
          These collaborative experiences later contributed to a published study
          exploring hybrid auditory-perceptual voice evaluation training workflows combining
          expert-led discussion,
          repeated evaluation,
          and optional online practice<Citation id="ref5" />.
        </p>

        <Image
          src="/images/insights/2026/two-years-of-listening/Montreal.png"
          caption="One of the first collaborative perceptual training experiences carried out with students at Université de Montréal using All-Voiced. Photo: Ingrid Verduyckt."
        />

        <p>
          Gradually,
          the platform also started being adopted across increasingly diverse academic
          and clinical contexts,
          with users and institutions from multiple countries beginning to participate
          in shared perceptual workflows.
        </p>

        <Quote>
          Listening was no longer functioning only as an act.  
          It was starting to function as infrastructure.
        </Quote>

        <h2>
          Looking back from today
        </h2>

        <p>
          Today, as a new collaborative experience begins with Université de Montréal
          using a substantially more robust infrastructural model,
          I find myself thinking back to those first sliders released in April 2024.
        </p>

        <p>
          At the time,
          the goal felt relatively simple:
          creating digital environments that could make auditory-perceptual voice
          evaluation easier to teach,
          organize,
          and explore.
        </p>

        <p>
          I did not yet realize that the deeper challenge was not only perceptual
          evaluation itself,
          but the absence of infrastructures capable of supporting listening as a
          cumulative scientific activity.
        </p>

        <p>
          Two years later,
          I no longer see All-Voiced simply as a collection of perceptual tools,
          but increasingly as an evolving infrastructure for perceptual voice
          evaluation across education,
          research,
          calibration,
          and distributed human-centered evaluation workflows.
        </p>

        <Quote>
          The paper forms,
          rulers,
          spreadsheets,
          and envelopes were never really the central problem.

          They were simply revealing the absence of infrastructure.
        </Quote>

        <References items={references} />
      </>
    );
  },
};